/**
 * Bond price, yield to maturity, current yield and duration, following Excel's PRICE,
 * YIELD, DURATION and MDURATION (securities that pay periodic interest, par 100).
 *
 * With N coupons left between settlement and maturity, f coupons a year, c = 100 × rate ÷ f
 * per coupon, A = days from the last coupon date to settlement, E = days in the coupon
 * period containing settlement and DSC = days from settlement to the next coupon:
 *
 *   N > 1:  price = redemption ÷ (1 + y/f)^(N−1+DSC/E)
 *                   + Σ(k=1..N) c ÷ (1 + y/f)^(k−1+DSC/E) − c × A/E
 *   N = 1:  price = (redemption + c) ÷ (1 + (DSC/E) × y/f) − c × A/E
 *
 * The price is the clean price (net of accrued interest c × A/E). Yield is found by
 * bisection, so it is exact where Excel's iteration stops at about 1e-7. Macaulay duration
 * is Σ t × PV ÷ Σ PV in years, with t = (k−1+DSC/E) ÷ f; modified duration divides by
 * (1 + y/f).
 *
 * Day-count basis (the `basis` argument): 0 = US (NASD) 30/360, 1 = actual/actual,
 * 2 = actual/360, 3 = actual/365, 4 = European 30/360. As in Excel, A is counted on that
 * basis, E is 360÷f (bases 0, 2, 4), 365÷f (basis 3) or the actual days between coupon
 * dates (basis 1), and for the 30/360 bases DSC = E − A, otherwise actual days.
 */

export interface Ymd {
  y: number;
  m: number;
  d: number;
}

export type Basis = 0 | 1 | 2 | 3 | 4;
export type Frequency = 1 | 2 | 4;

export const BASES: readonly Basis[] = [0, 1, 2, 3, 4];
export const FREQUENCIES: readonly Frequency[] = [1, 2, 4];

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;

/** Parses "YYYY-MM-DD"; null when malformed or not a real calendar date. */
export function parseIsoDate(text: string): Ymd | null {
  const match = ISO_DATE.exec(text.trim());
  if (!match) {
    return null;
  }
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) {
    return null;
  }
  return { y, m, d };
}

export function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Days since the Unix epoch, in UTC, so day differences are exact. */
export function serial(date: Ymd): number {
  return Date.UTC(date.y, date.m - 1, date.d) / MS_PER_DAY;
}

function isLastDayOfMonth(date: Ymd): boolean {
  return date.d === daysInMonth(date.y, date.m);
}

function isLastDayOfFebruary(date: Ymd): boolean {
  return date.m === 2 && isLastDayOfMonth(date);
}

function days360(start: Ymd, end: Ymd, european: boolean): number {
  let d1 = start.d;
  let d2 = end.d;
  if (european) {
    d1 = Math.min(d1, 30);
    d2 = Math.min(d2, 30);
  } else {
    if (isLastDayOfFebruary(start) && isLastDayOfFebruary(end)) {
      d2 = 30;
    }
    if (isLastDayOfFebruary(start)) {
      d1 = 30;
    }
    if (d2 === 31 && d1 >= 30) {
      d2 = 30;
    }
    if (d1 === 31) {
      d1 = 30;
    }
  }
  return (end.y - start.y) * 360 + (end.m - start.m) * 30 + (d2 - d1);
}

/** The coupon date `steps` coupon periods before maturity (month-end rule as in Excel). */
export function couponDateBefore(
  maturity: Ymd,
  steps: number,
  frequency: Frequency
): Ymd {
  const months = steps * (12 / frequency);
  const total = maturity.y * 12 + (maturity.m - 1) - months;
  const y = Math.floor(total / 12);
  const m = (total % 12) + 1;
  const last = daysInMonth(y, m);
  const d = isLastDayOfMonth(maturity) ? last : Math.min(maturity.d, last);
  return { y, m, d };
}

export interface CouponFacts {
  previous: Ymd;
  next: Ymd;
  /** Coupons still to be paid, including the one at maturity. */
  n: number;
  /** Days from the previous coupon to settlement. */
  a: number;
  /** Days in the coupon period containing settlement. */
  e: number;
  /** Days from settlement to the next coupon. */
  dsc: number;
}

export function couponFacts(
  settlement: Ymd,
  maturity: Ymd,
  frequency: Frequency,
  basis: Basis
): CouponFacts {
  let k = 1;
  while (
    serial(couponDateBefore(maturity, k, frequency)) > serial(settlement)
  ) {
    k += 1;
  }
  const previous = couponDateBefore(maturity, k, frequency);
  const next = couponDateBefore(maturity, k - 1, frequency);
  const thirty = basis === 0 || basis === 4;
  const a = thirty
    ? days360(previous, settlement, basis === 4)
    : serial(settlement) - serial(previous);
  let e = 360 / frequency;
  if (basis === 1) {
    e = serial(next) - serial(previous);
  } else if (basis === 3) {
    e = 365 / frequency;
  }
  const dsc = thirty ? e - a : serial(next) - serial(settlement);
  return { previous, next, n: k, a, e, dsc };
}

export interface BondTerms {
  settlement: Ymd;
  maturity: Ymd;
  /** Annual coupon rate as a decimal. */
  rate: number;
  /** Redemption value per 100 of par. */
  redemption: number;
  frequency: Frequency;
  basis: Basis;
}

function dirtyPrice(facts: CouponFacts, terms: BondTerms, y: number): number {
  const { n, e, dsc } = facts;
  const f = terms.frequency;
  const c = (100 * terms.rate) / f;
  if (n === 1) {
    return (terms.redemption + c) / (1 + ((dsc / e) * y) / f);
  }
  const growth = 1 + y / f;
  let total = terms.redemption / growth ** (n - 1 + dsc / e);
  for (let k = 1; k <= n; k += 1) {
    total += c / growth ** (k - 1 + dsc / e);
  }
  return total;
}

/** Excel PRICE: clean price per 100 par at annual yield `y` (decimal). */
export function bondPrice(terms: BondTerms, y: number): number {
  const facts = couponFacts(
    terms.settlement,
    terms.maturity,
    terms.frequency,
    terms.basis
  );
  return (
    dirtyPrice(facts, terms, y) -
    ((100 * terms.rate) / terms.frequency) * (facts.a / facts.e)
  );
}

/** Accrued interest per 100 par. */
export function accruedInterest(terms: BondTerms): number {
  const facts = couponFacts(
    terms.settlement,
    terms.maturity,
    terms.frequency,
    terms.basis
  );
  return ((100 * terms.rate) / terms.frequency) * (facts.a / facts.e);
}

const YIELD_ITERATIONS = 300;
const YIELD_MAX = 100;

/**
 * Excel YIELD: the annual yield (decimal) that gives the clean `price` per 100 par.
 * Null when no yield between just above −f (a coupon period's growth factor of zero)
 * and 10,000% reproduces the price.
 */
export function bondYield(terms: BondTerms, price: number): number | null {
  const f = terms.frequency;
  const facts = couponFacts(terms.settlement, terms.maturity, f, terms.basis);
  const c = (100 * terms.rate) / f;
  const target = price + c * (facts.a / facts.e);
  if (!(price > 0)) {
    return null;
  }
  if (facts.n === 1) {
    return ((terms.redemption + c) / target - 1) * ((f * facts.e) / facts.dsc);
  }
  let lo = -f * 0.999_999;
  let hi = YIELD_MAX;
  if (
    dirtyPrice(facts, terms, lo) < target ||
    dirtyPrice(facts, terms, hi) > target
  ) {
    return null;
  }
  for (let i = 0; i < YIELD_ITERATIONS; i += 1) {
    const mid = (lo + hi) / 2;
    if (dirtyPrice(facts, terms, mid) > target) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return (lo + hi) / 2;
}

export interface CashFlowRow {
  /** Coupon number, 1 = the next coupon. */
  k: number;
  date: Ymd;
  cashFlow: number;
  /** Time in coupon periods from settlement. */
  periods: number;
  /** Time in years: periods ÷ frequency. */
  years: number;
  presentValue: number;
}

export function cashFlows(terms: BondTerms, y: number): CashFlowRow[] {
  const f = terms.frequency;
  const facts = couponFacts(terms.settlement, terms.maturity, f, terms.basis);
  const c = (100 * terms.rate) / f;
  const rows: CashFlowRow[] = [];
  for (let k = 1; k <= facts.n; k += 1) {
    const periods = k - 1 + facts.dsc / facts.e;
    const cashFlow = k === facts.n ? c + terms.redemption : c;
    const discount =
      facts.n === 1
        ? 1 + ((facts.dsc / facts.e) * y) / f
        : (1 + y / f) ** periods;
    rows.push({
      k,
      date: couponDateBefore(terms.maturity, facts.n - k, f),
      cashFlow,
      periods,
      years: periods / f,
      presentValue: cashFlow / discount,
    });
  }
  return rows;
}

export interface DurationResult {
  macaulay: number;
  modified: number;
}

/** Excel DURATION and MDURATION at annual yield `y`. */
export function bondDuration(terms: BondTerms, y: number): DurationResult {
  const rows = cashFlows(terms, y);
  let weighted = 0;
  let total = 0;
  for (const row of rows) {
    weighted += row.years * row.presentValue;
    total += row.presentValue;
  }
  const macaulay = weighted / total;
  return { macaulay, modified: macaulay / (1 + y / terms.frequency) };
}

/** Annual coupon per 100 par ÷ the clean price. */
export function currentYield(rate: number, cleanPrice: number): number {
  return (100 * rate) / cleanPrice;
}
